import { selectGroupsAndSubgroupsByKey } from '@databases/grouping/selectors/select-groups-and-subgroups-by-key';
import { selectSortedGroups } from '@databases/grouping/selectors/select-sorted-groups';
import { type DefaultRootState } from '@databases/redux';
import { type DatabaseViewSettings } from '@databases/types/database/view';

import { getTimelineEntryGroups } from './timeline-entry-groups.ts';
import { TIMELINE_LAYOUT_ID, timelineLayoutSchema } from './view-layout-module.ts';

/**
 * Projects the selections adapter onto Timeline's grouped display order.
 * Timeline keeps grouping in its typed property bag, so the generic view
 * projection cannot establish this order without a layout-owned adapter.
 */
export function getTimelineSelectionEntryIds({
  contentId,
  entryIds,
  state,
  viewId,
  viewSettings
}: {
  contentId: string;
  entryIds: readonly string[];
  state: DefaultRootState;
  viewId: string;
  viewSettings: DatabaseViewSettings;
}): readonly string[] {
  const parsedSettings = timelineLayoutSchema.safeParse(
    viewSettings.properties?.[TIMELINE_LAYOUT_ID]
  );
  const grouping = parsedSettings.success ? parsedSettings.data.grouping : undefined;

  if (!grouping?.groupBy) {
    return entryIds;
  }

  const groupingViewSettings = { ...viewSettings, grouping };
  const selectorOptions = { contentId, viewId, viewSettings: groupingViewSettings };
  const groups = selectSortedGroups(state, selectorOptions);

  if (groups.length === 0) {
    return entryIds;
  }

  const entriesByGroup = selectGroupsAndSubgroupsByKey(
    state,
    selectorOptions
  ).entriesByGroup;

  if (!entriesByGroup) {
    return entryIds;
  }

  return getTimelineEntryGroups({ entryIds, entriesByGroup, groups }).flatMap(
    ({ entryIds: groupEntryIds }) => groupEntryIds
  );
}
